(() => {
  'use strict';

  if (window.AiLabsPages) {
    window.AiLabsPages.mount();
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const controllers = new WeakMap();

  class AiLabsPage {
    constructor(root) {
      this.root = root;
      this.cleanup = [];
      this.raf = 0;
      this.init();
    }

    init() {
      if (!reducedMotion) {
        this.root.classList.add('is-animated');
        this.initHeroHeading();
        this.initEditorialHeading();
        this.initBuildRotation();
        this.initReveals();
        this.initLedger();
        this.initPointerFields();
      }
      this.initSteps();
      this.initBanner();
    }

    initHeroHeading() {
      const heading = this.root.querySelector('[data-ail-hero-heading]');
      if (!heading || heading.querySelector('.ail-hero-word')) return;

      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      let wordIndex = 0;
      textNodes.forEach((textNode) => {
        const fragment = document.createDocumentFragment();
        textNode.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }
          const word = document.createElement('span');
          word.className = 'ail-hero-word';
          word.style.setProperty('--ail-word-index', wordIndex);
          word.textContent = part;
          wordIndex += 1;
          fragment.appendChild(word);
        });
        textNode.replaceWith(fragment);
      });
      heading.classList.add('has-word-animation');
    }

    initEditorialHeading() {
      const heading = this.root.querySelector('.ail-editorial--center .ail-heading__title');
      if (!heading || heading.classList.contains('has-masked-lines')) return;

      const nodes = Array.from(heading.childNodes);
      const strong = nodes.find((node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'STRONG');
      const splitAt = nodes.indexOf(strong);
      if (splitAt < 1) return;

      const groups = [nodes.slice(0, splitAt), nodes.slice(splitAt)];
      if (groups.some((group) => !group.some((node) => node.textContent.trim()))) return;

      heading.replaceChildren();
      groups.forEach((group) => {
        const line = document.createElement('span');
        const inner = document.createElement('span');
        line.className = 'ail-editorial-line';
        inner.className = 'ail-editorial-line__inner';
        group.forEach((node) => inner.appendChild(node));
        line.appendChild(inner);
        heading.appendChild(line);
      });
      heading.classList.add('has-masked-lines');
    }

    initBuildRotation() {
      const orient = this.root.querySelector('.ail-build__orient');
      if (!orient || !window.matchMedia('(hover: hover)').matches) return;

      let targetX = 0;
      let currentX = 0;
      let running = false;

      const tick = () => {
        currentX += (targetX - currentX) * 0.045;
        const rotateY = currentX * (currentX < 0 ? 26 : 32);
        orient.style.transform = `rotateY(${rotateY.toFixed(3)}deg)`;

        if (Math.abs(targetX - currentX) > 0.0005) {
          this.buildRaf = window.requestAnimationFrame(tick);
        } else {
          running = false;
          this.buildRaf = 0;
        }
      };

      const rotateBuild = (event) => {
        targetX = (event.clientX / window.innerWidth) * 2 - 1;
        if (running) return;
        running = true;
        this.buildRaf = window.requestAnimationFrame(tick);
      };

      this.listen(window, 'pointermove', rotateBuild, { passive: true });
    }

    listen(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      this.cleanup.push(() => target.removeEventListener(event, handler, options));
    }

    initReveals() {
      const elements = Array.from(this.root.querySelectorAll('.ail-reveal'));
      const isAda = this.root.matches('.ail-ada-hero, .ail-ada-section, .ail-banner');

      elements.forEach((element, index) => {
        let delay = 0;
        if (isAda) delay = (index % 3) * 90;
        if (element.classList.contains('ail-product-card')) {
          const cards = elements.filter((item) => item.classList.contains('ail-product-card'));
          delay = (cards.indexOf(element) % 3) * 90;
        }
        if (delay) element.style.setProperty('--ail-reveal-delay', `${delay}ms`);
      });

      if (!('IntersectionObserver' in window)) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
      }

      const pending = elements.filter((element) => {
        if (isAda && element.getBoundingClientRect().top < window.innerHeight * 0.9) {
          element.classList.add('is-visible');
          return false;
        }
        return true;
      });

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          }
        });
      }, isAda
        ? { threshold: 0.01, rootMargin: '0px 0px -12% 0px' }
        : { threshold: 0.15 });
      pending.forEach((element) => this.revealObserver.observe(element));
    }

    initLedger() {
      const rows = Array.from(this.root.querySelectorAll('[data-ail-ledger-row]'));
      if (!rows.length) return;

      const equalize = () => {
        rows.forEach((row) => { row.style.minHeight = ''; });
        if (window.innerWidth <= 760) return;
        const height = Math.max(...rows.map((row) => row.offsetHeight));
        rows.forEach((row) => { row.style.minHeight = `${height}px`; });
      };
      this.listen(window, 'resize', equalize);
      equalize();

      let index = 0;
      const show = () => {
        rows.forEach((row) => row.classList.remove('is-active'));
        rows[index].classList.add('is-active');
        this.ledgerTimer = window.setTimeout(hide, 1900);
      };
      const hide = () => {
        rows[index].classList.remove('is-active');
        index = (index + 1) % rows.length;
        this.ledgerTimer = window.setTimeout(show, 650);
      };
      show();
    }

    initSteps() {
      const track = this.root.querySelector('[data-ail-steps]');
      if (!track) return;
      const fill = track.querySelector('[data-ail-steps-fill]');
      const steps = Array.from(track.querySelectorAll('[data-ail-step]'));
      const update = () => {
        const rect = track.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.78 - rect.top) / (rect.height + window.innerHeight * 0.2)));
        if (fill) {
          if (window.matchMedia('(max-width: 989px)').matches) fill.style.height = `${progress * 100}%`;
          else fill.style.width = `${progress * 100}%`;
        }
        steps.forEach((step, index) => step.classList.toggle('is-active', progress >= (index + 0.4) / steps.length));
      };
      this.listen(window, 'scroll', () => this.requestUpdate(update), { passive: true });
      this.listen(window, 'resize', () => this.requestUpdate(update));
      update();
    }

    requestUpdate(callback) {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        callback();
        this.raf = 0;
      });
    }

    initPointerFields() {
      this.root.querySelectorAll('[data-ail-glow-field]').forEach((field) => {
        const cells = Array.from(field.children);
        const paint = (event) => {
          cells.forEach((cell) => {
            const box = cell.getBoundingClientRect();
            const distance = Math.hypot(event.clientX - box.left - box.width / 2, event.clientY - box.top - box.height / 2);
            const glow = Math.max(0, 1 - distance / 190);
            cell.style.setProperty('--ail-glow', (glow * glow).toFixed(3));
          });
          const box = field.getBoundingClientRect();
          const rotateX = ((event.clientY - box.top) / box.height - 0.5) * -9;
          const rotateY = ((event.clientX - box.left) / box.width - 0.5) * 11;
          field.style.transform = `rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg)`;
        };
        const reset = () => {
          cells.forEach((cell) => cell.style.setProperty('--ail-glow', '0'));
          field.style.transform = '';
        };
        this.listen(field, 'pointermove', paint, { passive: true });
        this.listen(field, 'pointerleave', reset);
      });
    }

    initBanner() {
      const field = this.root.querySelector('[data-ail-banner-field]');
      if (!field) return;
      if (!field.children.length) {
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < 180; index += 1) fragment.appendChild(document.createElement('i'));
        field.appendChild(fragment);
      }
      if (reducedMotion) return;

      const cells = Array.from(field.children);
      const paintPointer = (event) => {
        cells.forEach((cell) => {
          const box = cell.getBoundingClientRect();
          const distance = Math.hypot(event.clientX - box.left - box.width / 2, event.clientY - box.top - box.height / 2);
          const glow = Math.max(0, 1 - distance / 330);
          cell.style.setProperty('--ail-glow', (glow * glow).toFixed(3));
        });
      };
      const clearPointer = () => cells.forEach((cell) => cell.style.setProperty('--ail-glow', '0'));

      if (window.matchMedia('(hover: none)').matches || window.innerWidth <= 900) {
        const animate = (now) => {
          cells.forEach((cell) => {
            const x = cell.offsetLeft + cell.offsetWidth / 2;
            const y = cell.offsetTop + cell.offsetHeight / 2;
            const waveOne = 0.5 + 0.5 * Math.sin(now * 0.0019 + (x + y) * 0.013);
            const waveTwo = 0.5 + 0.5 * Math.sin(now * 0.0013 + x * 0.021 - y * 0.011);
            cell.style.setProperty('--ail-glow', (0.1 + 0.15 * waveOne + 0.11 * waveTwo).toFixed(3));
          });
          this.bannerRaf = window.requestAnimationFrame(animate);
        };
        this.bannerRaf = window.requestAnimationFrame(animate);
      } else {
        this.listen(this.root, 'pointermove', paintPointer, { passive: true });
        this.listen(this.root, 'pointerleave', clearPointer);
      }
    }

    destroy() {
      if (this.revealObserver) this.revealObserver.disconnect();
      if (this.ledgerTimer) window.clearTimeout(this.ledgerTimer);
      if (this.bannerRaf) window.cancelAnimationFrame(this.bannerRaf);
      if (this.buildRaf) window.cancelAnimationFrame(this.buildRaf);
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.cleanup.forEach((remove) => remove());
      controllers.delete(this.root);
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-ail-page]').forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new AiLabsPage(root));
    });
  };

  window.AiLabsPages = { mount };
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ail-page]').forEach((root) => controllers.get(root)?.destroy());
  });
})();
